import { Status } from "@/B2C/features/Policies/components/Status";
import { Button } from "@/shared/components/Button";
import { TextareaInput } from "@/shared/components/TextareaInput";
import { showToast } from "@/shared/lib/toast";
import { ReviewQueueRow } from "@/shared/redux/reducers/review-queue";
import { closeRightPanel } from "@/shared/redux/reducers/uiSlice";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";

type DetailSidePanelProps = {
  panelData: ReviewQueueRow;
};

const DetailSidePanel = ({ panelData }: DetailSidePanelProps): ReactNode => {
  const dispatch = useDispatch();
  const { control, watch } = useForm({ defaultValues: { reason: "" } });
  const reason = watch("reason") ?? "";

  const documents = [
    "PAN Card",
    "Banking Licence",
    "CA Registration Certificate",
    "Board Resolution",
    "GST Certificate",
  ];

  const kycChecks = [
    "PAN verification - MockVerify",
    "Aadhaar eKYC - MockVerify",
    "GST validation - MockVerify",
    "Bank penny-drop - MockVerify",
  ];

  const detailBlocks = [
    { label: "Licence no.", value: panelData?.channelCode ?? "-", key: "licenceNo" },
    {
      label: "Licence valid till",
      value: panelData?.licenceValidity ?? "-",
      key: "licenceValidity",
    },
    { label: "Channel code", value: panelData?.channelCode ?? "-", key: "channelCode" },
    {
      label: "Current status",
      value: <Status status={panelData?.status} />,
      key: "status",
    },
  ];

  const detailSections = [
    { title: "Documents", items: documents },
    { title: "KYC checks", items: kycChecks },
    { title: "Tie-ups", items: ["general – Meridian General"] },
  ];

  const canSubmit = reason.trim().length > 0;

  const handleAction = (type: "send-back" | "reject") => {
    if (!canSubmit) {
      showToast("Please add a reason or comment before sending back or rejecting.", "error");
      return;
    }

    const successMessage =
      type === "send-back"
        ? "Applicant sent back successfully."
        : "Applicant rejected successfully.";

    showToast(successMessage, "success");
    dispatch(closeRightPanel());
  };

  return (
    <div className="space-y-2 p-0 text-sm text-gray-700">
      {/* Basic Details */}
      <div className="grid grid-cols-2 gap-2">
        {detailBlocks.map(({ label, value }) => (
          <MiniInfoCard key={label} label={label} value={value} />
        ))}
      </div>

      {detailSections.map(({ title, items }) => (
        <DetailSection key={title} title={title}>
          {title === "Tie-ups" ? (
            <div className="rounded-lg bg-gray-50 px-3 py-2 text-gray-700">{items[0]}</div>
          ) : (
            <ul className="space-y-2">
              {items.map((item) => (
                <CheckItem key={item} label={item} />
              ))}
            </ul>
          )}
        </DetailSection>
      ))}

      <div className="rounded-xl border border-gray-200 bg-white p-2.5">
        <TextareaInput
          control={control}
          name="reason"
          label="Reason / comment"
          placeholder="Enter reason or comment for send back / reject"
          rows={3}
          className="w-full"
        />
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-2.5 text-sm text-amber-800">
        A mandatory document is missing or failed verification — approval is blocked.
      </div>

      <div className="flex gap-2 pt-0">
        <Button
          type="button"
          variant="primary"
          onClick={() => console.log("Approve", panelData)}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 shadow-sm"
        >
          Approve & activate
        </Button>

        <Button
          type="button"
          variant="secondary"
          onClick={() => handleAction("send-back")}
          disabled={!canSubmit}
          className="flex-1 rounded-xl border border-gray-300 px-3 py-2.5 text-sm font-medium text-gray-700 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          Send back
        </Button>

        <Button
          type="button"
          variant="danger"
          onClick={() => handleAction("reject")}
          disabled={!canSubmit}
          className="flex-1 rounded-xl px-3 py-2.5 text-sm font-semibold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
        >
          Reject
        </Button>
      </div>
    </div>
  );
};

type MiniInfoCardProps = {
  label: string;
  value: ReactNode;
};

const MiniInfoCard = ({ label, value }: MiniInfoCardProps) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-gray-50 p-2.5">
      <div className="text-[11px] text-gray-500">{label}</div>
      <div className="mt-1 text-sm font-semibold text-gray-800">{value}</div>
    </div>
  );
};

type DetailSectionProps = {
  title: string;
  children: ReactNode;
};

const DetailSection = ({ title, children }: DetailSectionProps) => {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-2.5">
      <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
        {title}
      </div>

      {children}
    </div>
  );
};

type CheckItemProps = {
  label: string;
};

const CheckItem = ({ label }: CheckItemProps) => {
  return (
    <li className="flex items-center justify-between rounded-lg bg-gray-50 px-2 py-1.5">
      <span className="text-xs text-gray-700">{label}</span>

      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full bg-emerald-100 text-[9px] text-emerald-700">
        ✓
      </span>
    </li>
  );
};

export default DetailSidePanel;
